import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const SUPPORT_ROLES = [
  {
    initials: "JD",
    name: "Jazzino Davies",
    role: "Child Therapist",
    copy: "Jazzino will support formulation, therapeutic consultation, reflective practice and direct work where this is agreed in the child's plan. He will share relevant safeguarding information and will not replace statutory health services or management decision-making.",
    color: "#696DFF",
    testid: "person-jazzino",
  },
  {
    initials: "MM",
    name: "Michael Morrison",
    role: "Training Director and Senior Residential Worker",
    copy: "Michael will coordinate induction, the training matrix, refresher learning, competency checks, reflective debriefs and senior practice support. Physical-intervention training will only be delivered by a provider whose child-specific accreditation and programme have been verified and approved by the Registered Manager.",
    color: "#5CFFAF",
    testid: "person-michael",
  },
  {
    initials: "JC",
    name: "Jade Chung",
    role: "Business Manager",
    copy: "Jade will support safer-recruitment administration, personnel and training records, document control, compliance tracking and operational systems. Business support will remain distinct from care, safeguarding and placement decisions, which sit with the Registered Manager and provider.",
    color: "#FFB019",
    testid: "person-jade",
  },
];

const CHART_ROWS = [
  {
    id: "row-1",
    people: [
      { initials: "SC", name: "Sandra Cornwall", role: "Responsible Individual", tone: "bg-ink text-white", testid: "chart-sandra" },
      { initials: "JC", name: "Jade Chung", role: "Business Manager", tone: "bg-sand text-ink", testid: "chart-jade" },
    ],
  },
  {
    id: "row-2",
    people: [
      { initials: "MC", name: "Munyaradzi Fortune Chigiji", role: "Registered Manager", tone: "bg-ink text-white", testid: "chart-munyaradzi" },
    ],
  },
  {
    id: "row-3",
    people: [
      { initials: "JD", name: "Jazzino Davies", role: "Child Therapist", dotted: true, tone: "bg-sky/10 text-ink", testid: "chart-jazzino" },
      { initials: "MM", name: "Michael Morrison", role: "Training Director, Senior Residential Worker", tone: "bg-sand text-ink", testid: "chart-michael" },
    ],
  },
  {
    id: "row-4",
    people: [
      { initials: "NM", name: "Night Manager", role: "Name to be confirmed", tone: "bg-sand text-ink", testid: "chart-night-manager" },
      { initials: "RSW", name: "Residential Support Workers", role: "Staff team including two overnight posts. Names to be confirmed", tone: "bg-sand text-ink", testid: "chart-rsw" },
    ],
  },
];

const OPERATIONS = [
  {
    title: "Day",
    copy: "Three care staff support the child, with capacity to deploy a fourth when current assessment identifies increased risk or need.",
    color: "#FF508D",
    testid: "ops-day",
  },
  {
    title: "Overnight",
    copy: "One Night Manager and two Residential Support Workers are planned: the Night Manager and one worker remain awake; the second worker undertakes the sleep-in duty.",
    color: "#696DFF",
    testid: "ops-overnight",
  },
  {
    title: "Management",
    copy: "The Registered Manager remains outside the operational ratio. Vacant or unnamed roles will be filled through safer recruitment and vetting before deployment.",
    color: "#FFB019",
    testid: "ops-management",
  },
];

const ChartNode = ({ initials, name, role, tone, dotted, testid }) => (
  <div
    data-testid={testid}
    className="flex w-full max-w-xs items-center gap-4 rounded-2xl border border-line bg-white px-5 py-4 text-left shadow-[0_1px_2px_rgba(45,55,72,0.05)]"
  >
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-extrabold ${tone}`}>
      {initials}
    </span>
    <span>
      <span className="block text-sm font-extrabold leading-snug text-ink">{name}</span>
      <span className="mt-0.5 block text-xs leading-snug text-mute">
        {role}
        {dotted && <em className="ml-1 not-italic text-sky">• clinical advice</em>}
      </span>
    </span>
  </div>
);

export default function Team() {
  return (
    <div data-testid="page-team">
      <PageHero
        testid="team-hero"
        eyebrow="Accountability from provider to practice"
        title={[
          "Leadership and",
          <>
            <em className="text-rainbow italic">governance</em>
          </>,
        ]}
        lead="The proposed leadership model separates provider oversight, day-to-day management, therapeutic advice, workforce development and business support. This helps decisions remain accountable and prevents specialist or administrative roles from replacing the Registered Manager's statutory responsibilities."
      />

      {/* Proposed Registered Manager feature + image */}
      <section className="container-x" data-testid="rm-section">
        <div>
          <FadeUp className="w-full">
            <div className="card edge-rainbow h-full p-8 sm:p-10" data-testid="rm-card">
              <div className="flex flex-wrap items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-ink text-sm font-extrabold text-white">MC</span>
                <div>
                  <h2 className="font-display text-2xl text-ink sm:text-3xl" data-testid="rm-name">Munyaradzi Fortune Chigiji</h2>
                  <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">Registered Manager</p>
                </div>
              </div>
              <div className="mt-6 space-y-4 text-base leading-relaxed text-mute" data-testid="rm-copy">
                <p>
                  Munyaradzi brings more than five years' experience supporting children and young
                  people, including those with autism, emotional and behavioural difficulties and
                  court-authorised restrictions. Across Support Worker, Team Leader and Deputy
                  Manager roles, he has contributed to opening new services, Ofsted registration
                  activity and the growth of provision from two to five registered settings.
                </p>
                <p>
                  He has completed the Level 5 Diploma in Leadership and Management for Residential
                  Childcare and holds a bachelor's degree. As the home's Registered Manager,
                  Munyaradzi will be accountable for care planning, safeguarding, staffing decisions,
                  supervision, notifications, partnership working, quality assurance and the culture
                  of the home. He will work on a separate management rota and will not be counted in
                  the child's operational staffing ratio.
                </p>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* Other roles */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="roles-section">
        <FadeUp>
          <p className="eyebrow">The wider team</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="roles-title">
            Clear roles, kept deliberately distinct
          </h2>
        </FadeUp>
        <StaggerGroup className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          <StaggerItem className="sm:col-span-2 lg:col-span-1">
            <div className="card h-full p-8" data-testid="person-sandra">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-coral/15 text-sm font-extrabold text-ink">SC</span>
              <h3 className="mt-6 font-display text-2xl text-ink">Sandra Cornwall</h3>
              <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">Responsible Individual</p>
              <p className="mt-4 text-base leading-relaxed text-mute" data-testid="sandra-copy">
                Sandra will provide provider-level oversight and challenge. She will monitor whether
                the home has sufficient resources, suitable staffing, safe premises and effective
                quality-assurance systems. Sandra will meet regularly with the Registered Manager,
                review significant incidents, complaints, safeguarding themes, workforce risks and
                Regulation 45 findings, and ensure that actions are completed. Her role is to test
                the quality of leadership and secure organisational support without taking over
                daily management.
              </p>
            </div>
          </StaggerItem>
          {SUPPORT_ROLES.map(({ initials, name, role, copy, color, testid }) => (
            <StaggerItem key={name}>
              <div className="card h-full p-8" data-testid={testid}>
                <span className="flex h-14 w-14 items-center justify-center rounded-full text-sm font-extrabold text-ink" style={{ background: `${color}26` }}>
                  {initials}
                </span>
                <h3 className="mt-6 font-display text-2xl text-ink">{name}</h3>
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">{role}</p>
                <p className="mt-4 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Management assurance */}
      <section className="container-x" data-testid="assurance-section">
        <FadeUp>
          <div className="relative overflow-hidden rounded-[2.5rem] bg-sand px-7 py-12 sm:px-14 sm:py-16">
            <p className="eyebrow">Management assurance</p>
            <p className="mt-6 max-w-4xl text-lg leading-relaxed text-ink/90" data-testid="assurance-copy">
              Oversight will include daily handovers, scheduled supervision, team meetings, incident
              and restraint review, medication and record audits, training compliance, feedback from
              the child and professionals, independent Regulation 44 visits and the Registered
              Person's Regulation 45 quality-of-care review. Learning will be recorded, assigned to
              a named person and checked for completion.
            </p>
            <p className="mt-8 font-display text-2xl italic text-ink/80" data-testid="assurance-highlight">
              Clear reporting and professional advice.
            </p>
          </div>
        </FadeUp>
      </section>

      {/* Org structure */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="structure-section">
        <FadeUp>
          <p className="eyebrow">Team structure and accountability</p>
          <h2 className="mt-4 max-w-2xl font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="structure-title">
            The proposed organisational structure
          </h2>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-mute" data-testid="structure-copy">
            The chart shows the proposed application-stage structure. Solid lines identify
            operational accountability; the dotted line identifies clinical or professional advice.
            Therapeutic input supports, but does not override, the Registered Manager's
            accountability for the home.
          </p>
        </FadeUp>

        <FadeUp delay={0.1}>
          <div className="card mt-12 p-8 sm:p-10" data-testid="org-chart">
            <p className="text-center text-xs font-extrabold uppercase tracking-[0.22em] text-mute">
              Organisational structure — Infinity &amp; Beyond Care
            </p>
            <div className="mt-8 flex flex-col items-center gap-0">
              {CHART_ROWS.map((row, r) => (
                <div key={row.id} className="flex w-full flex-col items-center">
                  {r > 0 && (
                    <div
                      aria-hidden="true"
                      className="h-8 w-px bg-line"
                      style={CHART_ROWS[r].people.some((p) => p.dotted) && CHART_ROWS[r].people.length === 1 ? { background: "none", borderLeft: "2px dashed #696DFF" } : undefined}
                    />
                  )}
                  <div className={`flex w-full flex-wrap items-start justify-center gap-4 ${row.people.length > 1 ? "sm:gap-16" : ""}`}>
                    {row.people.map((p) => (
                      <ChartNode key={p.testid} {...p} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-xs font-bold text-mute" data-testid="chart-legend">
              <span className="flex items-center gap-2">
                <span className="h-px w-8 bg-line" aria-hidden="true" /> — Operational reporting
              </span>
              <span className="flex items-center gap-2">
                <span className="h-0 w-8 border-t-2 border-dashed border-sky" aria-hidden="true" /> — Clinical or professional advice
              </span>
            </div>
          </div>
        </FadeUp>

        <StaggerGroup className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {OPERATIONS.map(({ title, copy, color, testid }) => (
            <StaggerItem key={title}>
              <div className="card h-full p-8" data-testid={testid}>
                <span className="dot" style={{ background: color }} aria-hidden="true" />
                <h3 className="mt-4 font-display text-2xl text-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>

        <FadeUp>
          <p className="mt-8 max-w-4xl text-sm leading-relaxed text-mute" data-testid="structure-disclaimer">
            Application-stage structure. Final deployment remains subject to safer recruitment,
            matching, training, competency assessment and the needs of the child placed.
          </p>
        </FadeUp>
      </section>

      <div className="container-x pb-4">
        <NextPage
          to="/referrals"
          title="Referral, matching and contact"
          label="The five-step process and how to reach us"
          testid="next-referrals"
        />
      </div>
    </div>
  );
}
